import { Button, ConfirmDialog, Modal, Select, Surface, Textarea } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SalesOrderListRow, SalesOrderStatus } from '@warehouse/shared';
import { Plus } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { usePermission } from '../../../auth/auth-store';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { ORDER_QUERY_KEYS, portalApi, salesOrdersApi } from '../api';
import { DeliveryCalendar } from '../components/DeliveryCalendar';
import { OrderLineEditor, type EditableLine } from '../components/OrderLineEditor';
import { OrderStatusBadge } from '../components/StatusBadge';

/**
 * The admin sales-order list (spec §5.1).
 *
 * Two views over the same query: a flat order list with full search/filter/sort
 * (the shared `DataTable` + `useListState`, so the URL round-trips), and the
 * per-restaurant + delivery-date grouping the warehouse actually works in —
 * which is also what the bulk status transitions operate on.
 */

const BULK_TARGETS: Exclude<SalesOrderStatus, 'cancelled'>[] = ['pagaende', 'packat', 'skickat'];

export function SalesOrdersPage(): ReactNode {
  const { t } = useTranslation(['salesOrders', 'common']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canManage = usePermission('orders.outbound.manage');
  const canCreate = usePermission('orders.outbound.create');

  const { state, setState, setFilter } = useListState({ defaultSort: 'deliveryDate', defaultDir: 'desc' });
  const [view, setView] = useState<'orders' | 'groups'>('orders');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkTarget, setBulkTarget] = useState<Exclude<SalesOrderStatus, 'cancelled'> | null>(null);
  const [creating, setCreating] = useState(false);

  const listQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.list(state),
    queryFn: () => salesOrdersApi.list(state),
  });
  const groupQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.groups(state),
    queryFn: () => salesOrdersApi.groups(state),
    enabled: view === 'groups',
  });
  const restaurantsQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.restaurants(),
    queryFn: () => portalApi.restaurants(),
  });

  useLiveQuery(['sales-orders'], ['sales-orders']);

  const bulkMutation = useMutation({
    mutationFn: (input: Parameters<typeof salesOrdersApi.bulkStatus>[0]) => salesOrdersApi.bulkStatus(input),
    onSuccess: (result) => {
      toastSuccess(t('salesOrders:bulk.done', { updated: result.updated, skipped: result.skipped }));
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const rows = listQuery.data?.items ?? [];
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));

  function toggle(id: string): void {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const columns = useMemo<DataTableColumn<SalesOrderListRow>[]>(() => {
    const base: DataTableColumn<SalesOrderListRow>[] = [
      {
        id: 'orderNo',
        header: t('salesOrders:list.columns.orderNo'),
        sortField: 'orderNo',
        cell: (row) => <span className="font-mono text-xs">{row.orderNo}</span>,
      },
      {
        id: 'restaurant',
        header: t('salesOrders:list.columns.restaurant'),
        sortField: 'restaurant',
        cell: (row) => (
          <span>
            {row.restaurantName}
            <span className="ml-2 font-mono text-xs text-ink-muted">{row.restaurantCode}</span>
          </span>
        ),
      },
      {
        id: 'status',
        header: t('salesOrders:list.columns.status'),
        sortField: 'status',
        cell: (row) => <OrderStatusBadge status={row.status} />,
      },
      {
        id: 'deliveryDate',
        header: t('salesOrders:list.columns.deliveryDate'),
        sortField: 'deliveryDate',
        cell: (row) => row.deliveryDate,
      },
      {
        id: 'lines',
        header: t('salesOrders:list.columns.lines'),
        align: 'right',
        cell: (row) => row.lineCount,
        hideBelow: 'md',
      },
      {
        id: 'source',
        header: t('salesOrders:list.columns.source'),
        cell: (row) => t(`salesOrders:source.${row.source}`),
        hideBelow: 'lg',
      },
      {
        id: 'placedBy',
        header: t('salesOrders:list.columns.placedBy'),
        cell: (row) => row.placedByName ?? '—',
        hideBelow: 'xl',
      },
    ];

    if (!canManage) return base;

    return [
      {
        id: 'select',
        width: 'w-10',
        header: (
          <input
            type="checkbox"
            aria-label={t('common:actions.selectAll')}
            checked={allSelected}
            onChange={() => {
              setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));
            }}
          />
        ),
        cell: (row) => (
          <input
            type="checkbox"
            aria-label={`${t('common:actions.select')} ${row.orderNo}`}
            checked={selected.has(row.id)}
            onClick={(event) => {
              event.stopPropagation();
            }}
            onChange={() => {
              toggle(row.id);
            }}
          />
        ),
      },
      ...base,
    ];
  }, [t, canManage, allSelected, rows, selected]);

  return (
    <>
      <PageHeader
        title={t('salesOrders:title')}
        subtitle={t('salesOrders:subtitle')}
        actions={
          canCreate ? (
            <Button
              icon={<Plus className="size-4" aria-hidden />}
              onClick={() => {
                setCreating(true);
              }}
            >
              {t('salesOrders:actions.newOrder')}
            </Button>
          ) : null
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button
          variant={view === 'orders' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => {
            setView('orders');
          }}
        >
          {t('salesOrders:list.view.orders')}
        </Button>
        <Button
          variant={view === 'groups' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => {
            setView('groups');
          }}
        >
          {t('salesOrders:list.view.groups')}
        </Button>
      </div>

      {view === 'orders' ? (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          state={state}
          onStateChange={setState}
          total={listQuery.data?.total ?? null}
          loading={listQuery.isLoading}
          error={listQuery.error}
          searchPlaceholder={t('salesOrders:list.searchPlaceholder')}
          emptyTitle={t('salesOrders:list.empty')}
          emptyDescription={t('salesOrders:list.emptyDescription')}
          onRowClick={(row) => {
            void navigate(`/orders/sales/${row.id}`);
          }}
          filters={
            <>
              <Select
                aria-label={t('salesOrders:list.filters.status')}
                value={(state.filters?.status as string) ?? ''}
                onChange={(event) => {
                  setFilter('status', event.target.value || undefined);
                }}
                options={[
                  { value: '', label: t('salesOrders:list.filters.allStatuses') },
                  ...(['ej_hanterad', 'pagaende', 'packat', 'skickat', 'cancelled'] as SalesOrderStatus[]).map(
                    (status) => ({ value: status, label: t(`salesOrders:status.${status}`) }),
                  ),
                ]}
              />
              <Select
                aria-label={t('salesOrders:list.filters.restaurant')}
                value={(state.filters?.restaurantId as string) ?? ''}
                onChange={(event) => {
                  setFilter('restaurantId', event.target.value || undefined);
                }}
                options={[
                  { value: '', label: t('salesOrders:list.filters.allRestaurants') },
                  ...(restaurantsQuery.data?.items ?? []).map((r) => ({ value: r.id, label: r.name })),
                ]}
              />
              <input
                type="date"
                aria-label={t('salesOrders:list.filters.deliveryDateFrom')}
                className="rounded-lg border border-surface-border px-2 py-1.5 text-sm"
                value={(state.filters?.deliveryDateFrom as string) ?? ''}
                onChange={(event) => {
                  setFilter('deliveryDateFrom', event.target.value || undefined);
                }}
              />
              <input
                type="date"
                aria-label={t('salesOrders:list.filters.deliveryDateTo')}
                className="rounded-lg border border-surface-border px-2 py-1.5 text-sm"
                value={(state.filters?.deliveryDateTo as string) ?? ''}
                onChange={(event) => {
                  setFilter('deliveryDateTo', event.target.value || undefined);
                }}
              />
            </>
          }
          toolbar={
            canManage && selected.size > 0 ? (
              <div className="flex flex-wrap items-center gap-2" data-testid="bulk-actions">
                <span className="text-sm text-ink-muted">
                  {t('salesOrders:list.selected', { count: selected.size })}
                </span>
                {BULK_TARGETS.map((status) => (
                  <Button
                    key={status}
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setBulkTarget(status);
                    }}
                  >
                    {t(`salesOrders:status.${status}`)}
                  </Button>
                ))}
              </div>
            ) : null
          }
        />
      ) : (
        <GroupList
          loading={groupQuery.isLoading}
          groups={groupQuery.data?.items ?? []}
          canManage={canManage}
          onBulk={(restaurantId, deliveryDate, status) => {
            bulkMutation.mutate({ restaurantId, deliveryDate, status });
          }}
        />
      )}

      {bulkTarget ? (
        <ConfirmDialog
          title={t('salesOrders:bulk.title')}
          message={t('salesOrders:bulk.message', {
            count: selected.size,
            status: t(`salesOrders:status.${bulkTarget}`),
          })}
          confirmLabel={t('salesOrders:bulk.confirm')}
          cancelLabel={t('common:actions.cancel')}
          onConfirm={async () => {
            await bulkMutation.mutateAsync({ orderIds: [...selected], status: bulkTarget });
            setBulkTarget(null);
          }}
          onClose={() => {
            setBulkTarget(null);
          }}
        />
      ) : null}

      {creating ? (
        <CreateOrderModal
          onClose={() => {
            setCreating(false);
          }}
          onCreated={(id) => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
            void navigate(`/orders/sales/${id}`);
          }}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------

function GroupList({
  groups,
  loading,
  canManage,
  onBulk,
}: {
  groups: { restaurantId: string; restaurantName: string; restaurantCode: string; deliveryDate: string; orderCount: number; lineCount: number; statuses: Record<SalesOrderStatus, number> }[];
  loading: boolean;
  canManage: boolean;
  onBulk: (restaurantId: string, deliveryDate: string, status: Exclude<SalesOrderStatus, 'cancelled'>) => void;
}): ReactNode {
  const { t } = useTranslation(['salesOrders', 'common']);

  if (loading) return <p className="text-sm text-ink-muted">{t('common:common.loading')}</p>;
  if (groups.length === 0) return <p className="text-sm text-ink-muted">{t('salesOrders:list.empty')}</p>;

  return (
    <div className="flex flex-col gap-2" data-testid="order-groups">
      {groups.map((group) => (
        <Surface key={`${group.restaurantId}:${group.deliveryDate}`} className="flex flex-wrap items-center gap-4 p-4">
          <div className="min-w-48 flex-1">
            <p className="font-medium">{group.restaurantName}</p>
            <p className="text-xs text-ink-muted">
              {group.deliveryDate} ·{' '}
              {t('salesOrders:list.groupSummary', { orders: group.orderCount, lines: group.lineCount })}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(Object.keys(group.statuses) as SalesOrderStatus[])
              .filter((status) => group.statuses[status] > 0)
              .map((status) => (
                <span key={status} className="flex items-center gap-1 text-xs text-ink-muted">
                  <OrderStatusBadge status={status} />× {group.statuses[status]}
                </span>
              ))}
          </div>
          {canManage ? (
            <div className="flex flex-wrap gap-2">
              {BULK_TARGETS.map((status) => (
                <Button
                  key={status}
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    onBulk(group.restaurantId, group.deliveryDate, status);
                  }}
                >
                  {t(`salesOrders:status.${status}`)}
                </Button>
              ))}
            </div>
          ) : null}
        </Surface>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------

function CreateOrderModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}): ReactNode {
  const { t } = useTranslation(['salesOrders', 'common']);
  const [restaurantId, setRestaurantId] = useState('');
  const [deliveryDate, setDeliveryDate] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<EditableLine[]>([]);

  const restaurantsQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.restaurants(),
    queryFn: () => portalApi.restaurants(),
  });

  const categoryIds: string[] = [];
  const datesQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.deliveryDates(restaurantId, categoryIds),
    queryFn: () => portalApi.deliveryDates(restaurantId, categoryIds),
    enabled: restaurantId.length > 0,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      salesOrdersApi.create({
        restaurantId,
        deliveryDate: deliveryDate ?? '',
        ...(note.trim() ? { note: note.trim() } : {}),
        lines: lines.map((l) => ({ articleId: l.articleId, qty: l.qty, ...(l.unitId ? { unitId: l.unitId } : {}) })),
      }),
    onSuccess: (order) => {
      toastSuccess(t('salesOrders:create.created', { orderNo: order.orderNo }));
      onCreated(order.id);
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const ready = restaurantId.length > 0 && deliveryDate !== null && lines.length > 0;

  return (
    <Modal open onClose={onClose} title={t('salesOrders:create.title')} width={720}>
      <div className="flex flex-col gap-4">
        <Select
          label={t('salesOrders:create.restaurant')}
          placeholder={t('salesOrders:create.restaurantPlaceholder')}
          value={restaurantId}
          onChange={(event) => {
            setRestaurantId(event.target.value);
            setDeliveryDate(null);
          }}
          options={(restaurantsQuery.data?.items ?? []).map((r) => ({ value: r.id, label: r.name }))}
        />

        {restaurantId ? (
          <div>
            <p className="mb-2 text-sm font-medium">{t('salesOrders:create.deliveryDate')}</p>
            <DeliveryCalendar
              dates={datesQuery.data?.dates ?? []}
              passed={datesQuery.data?.passed ?? []}
              scheduleMissing={datesQuery.data?.scheduleMissing ?? false}
              loading={datesQuery.isLoading}
              value={deliveryDate}
              onChange={setDeliveryDate}
            />
          </div>
        ) : null}

        <div>
          <p className="mb-2 text-sm font-medium">{t('salesOrders:detail.linesTitle')}</p>
          <OrderLineEditor lines={lines} onChange={setLines} />
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('salesOrders:create.note')}</span>
          <Textarea
            rows={2}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        </label>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            disabled={!ready}
            loading={createMutation.isPending}
            onClick={() => {
              createMutation.mutate(undefined);
            }}
          >
            {t('salesOrders:create.submit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
